import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  TextField,
  BlockStack,
  InlineStack,
  Text,
  Checkbox,
  Banner,
} from '@shopify/polaris';

import { useStore } from '../providers/StoreProvider';
import { experimentApi } from '../utils/api';
import AppLayout from '../components/AppLayout';

function ExperimentEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { bars, fetchExperiments } = useStore();

  const [name, setName] = useState('');
  const [targetCountry, setTargetCountry] = useState('');
  const [selectedBarIds, setSelectedBarIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(isEditing);

  // Load existing experiment data
  useEffect(() => {
    if (!isEditing) return;
    const load = async () => {
      try {
        const data = await experimentApi.get(id);
        setName(data.name || '');
        setTargetCountry(data.target_country || '');
        setSelectedBarIds(data.bar_ids || []);
      } catch (err) {
        setError('Failed to load experiment');
      }
      setIsLoading(false);
    };
    load();
  }, [id, isEditing]);

  const toggleBar = useCallback((barId) => {
    setSelectedBarIds(prev =>
      prev.includes(barId)
        ? prev.filter(id => id !== barId)
        : [...prev, barId]
    );
  }, []);

  const handleSave = useCallback(async () => {
    setError(null);

    if (!name.trim()) {
      setError('Experiment name is required');
      return;
    }
    if (selectedBarIds.length < 2) {
      setError('Select at least 2 bars to compare');
      return;
    }

    setIsSaving(true);
    try {
      await experimentApi.create({
        name: name.trim(),
        bar_ids: selectedBarIds,
        target_country: targetCountry.trim() || null,
      });
      await fetchExperiments();
      navigate('/app/experiments');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create experiment';
      setError(msg);
    }
    setIsSaving(false);
  }, [name, selectedBarIds, targetCountry, fetchExperiments, navigate]);

  if (isLoading) {
    return (
      <AppLayout>
        <Page title="Loading..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Page
        title={isEditing ? 'Edit Experiment' : 'Create Experiment'}
        backAction={{ content: 'Back', onAction: () => navigate('/app/experiments') }}
        primaryAction={{
          content: isEditing ? 'Save' : 'Create',
          onAction: handleSave,
          loading: isSaving,
          disabled: isEditing, // editing not supported yet, only create
        }}
      >
        <Layout>
          {error && (
            <Layout.Section>
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            </Layout.Section>
          )}

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <TextField
                  label="Experiment name"
                  value={name}
                  onChange={setName}
                  placeholder="e.g., US Free Shipping Bar Test"
                  autoComplete="off"
                />
                <TextField
                  label="Target country (optional)"
                  value={targetCountry}
                  onChange={setTargetCountry}
                  placeholder="e.g., US"
                  helpText="ISO country code. Leave empty to include all visitors."
                  autoComplete="off"
                  maxLength={2}
                />
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Select bars to compare</Text>
                <Text tone="subdued">
                  Choose at least 2 bars. Each visitor will randomly see one of the selected bars.
                </Text>
                {bars.length === 0 ? (
                  <Banner tone="warning">
                    <p>No bars found. Create some shipping bars first.</p>
                  </Banner>
                ) : (
                  <BlockStack gap="200">
                    {bars.map(bar => (
                      <InlineStack key={bar.id} gap="200" blockAlign="center">
                        <Checkbox
                          label={
                            <InlineStack gap="200" blockAlign="center">
                              <Text>{bar.name}</Text>
                              <Text tone="subdued" variant="bodySm">
                                {bar.is_active ? 'Active' : 'Inactive'}
                              </Text>
                            </InlineStack>
                          }
                          checked={selectedBarIds.includes(bar.id)}
                          onChange={() => toggleBar(bar.id)}
                        />
                      </InlineStack>
                    ))}
                  </BlockStack>
                )}
                {selectedBarIds.length > 0 && selectedBarIds.length < 2 && (
                  <Text tone="caution" variant="bodySm">
                    Select at least one more bar.
                  </Text>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </AppLayout>
  );
}

export default ExperimentEditor;
